import { ProjectId } from "./projects";
export interface SystemStory {
  question: string;
  instruction: string;
  principle: string;
  explanation: string;
  layers: { name: string; source: string; description: string }[];
}
export const systemStories: Record<ProjectId, SystemStory> = {
  tracker: {
    question: "Money moved.\nBut was it spent?",
    instruction:
      "Switch between a transfer and an expense. Follow what changes—and what should not.",
    principle: "One amount. Two different meanings.",
    explanation:
      "A transfer changes where your money lives. An expense changes how much you have. The interface can be simple because the model keeps those meanings separate.",
    layers: [
      {
        name: "Capture the intent",
        source: "TransactionEditor",
        description:
          "Amount, account, and entry type form a draft. A transfer also identifies its destination.",
      },
      {
        name: "Protect the meaning",
        source: "FinanceStore",
        description:
          "Same-currency transfers affect both accounts. Only expenses contribute to spending totals.",
      },
      {
        name: "Commit safely",
        source: "FinancePersistence",
        description:
          "Validate the next document, then write atomically before publishing new state.",
      },
    ],
  },
  counter: {
    question: "A label is fixed.\nA portion is not.",
    instruction:
      "Adjust the sample portion. Calories and macros scale together from the same reference amount.",
    principle: "One calculation. A consistent picture.",
    explanation:
      "Keep the label’s reference amount separate from the amount eaten. Preview the result before logging, then derive the daily view from consumed food.",
    layers: [
      {
        name: "Choose an amount",
        source: "Consumable",
        description:
          "The food retains its nutrition reference and measured portion. Weight and volume are never silently interchanged.",
      },
      {
        name: "Scale consistently",
        source: "macros(for:unit:)",
        description:
          "Normalize the measurement, divide by the reference size, then scale each nutrient by the same ratio.",
      },
      {
        name: "Reflect actual intake",
        source: "TodayNutritionSnapshot",
        description:
          "Derive daily totals from logged food. Pending meal plans remain separate from intake.",
      },
    ],
  },
  spotter: {
    question: "A tap is fleeting.\nA set should stay.",
    instruction:
      "Adjust a sample set, log it, then undo. The recorded volume follows saved sets, not the draft.",
    principle: "The record comes before the reflection.",
    explanation:
      "The active workout owns its recorded sets. Phone, Watch, and Live Activity surfaces reflect that session, with revision checks and duplicate protection for commands.",
    layers: [
      {
        name: "Express a command",
        source: "WorkoutAction",
        description:
          "Logging, undo, and target adjustments are explicit actions against the active workout.",
      },
      {
        name: "Validate the session",
        source: "TrainingStore",
        description:
          "Check the current revision, update the set, and persist the session. Stale commands cannot log twice.",
      },
      {
        name: "Reflect the record",
        source: "Core Data / platform snapshots",
        description:
          "Saved workout state feeds the interface, Live Activity, and Watch synchronization contracts.",
      },
    ],
  },
};
